"use client";

import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslations } from "use-intl";

import { SparkleIcon } from "@/components/appIcons";
import { Button } from "@/components/ui/button";
import { useSession } from "@/hooks/use-session";
import { cn } from "@/lib/utils";
import { client } from "@/utils/orpc.client";

interface AdminOfferDiscoveryButtonProps {
  catalogId: string;
  entityType: "release" | "work";
  className?: string;
}

export function AdminOfferDiscoveryButton({
  catalogId,
  entityType,
  className,
}: AdminOfferDiscoveryButtonProps) {
  const t = useTranslations("globals.common");
  const { data: session } = useSession();
  const mutation = useMutation({
    mutationFn: () => client.deals.adminSubmitOfferDiscovery({ catalogId, entityType }),
    onSuccess: (result) => {
      toast.success(t("offerDiscoveryQueued", { count: result.submittedCount }));
    },
    onError: () => {
      toast.error(t("offerDiscoveryFailed"));
    },
  });

  if (session?.user.role !== "admin") return null;

  const label = mutation.isPending ? t("queueingOfferDiscovery") : t("queueOfferDiscovery");

  return (
    <Button
      variant="outline"
      size="icon"
      disabled={mutation.isPending}
      aria-busy={mutation.isPending}
      aria-label={label}
      title={label}
      onClick={() => mutation.mutate()}
      className={cn(
        "size-11 shrink-0 border-blue-600 bg-blue-600 text-white hover:bg-blue-700 hover:text-white",
        className,
      )}
    >
      <SparkleIcon className="text-[20px]" />
    </Button>
  );
}
